<template>
  <div>
    <dot-lottie-vue
      v-if="loading"
      autoplay
      loop
      style="margin: 0; height: 200px"
      src="animations/fetching.json"
    />
    <div class="w-full flex flex-row">
      <transition
        name="slide-fade"
        class="w-full flex flex-row"
      >
        <div
          v-if="!loading && tasks.length"
          class="w-full flex overflow-x-auto bg-white border-t-2 border-b-2 border-orange py-4 pl-6"
        >
          <div class="space-y-4">
            <h3>À examiner ({{ tasks.length }}{{ hasMoreTasks ? '+' : '' }})</h3>
            <div class="flex flex-row gap-6 pl-7">
              <transition-group>
                <div
                  v-for="(action, index) in tasks"
                  :key="`${action?.type}_${index}`"
                  class="list-action cursor-pointer"
                >
                  <ContextMenu>
                    <ContextMenuTrigger
                      as-child
                    >
                      <component
                        :is="resolveComponent(action)"
                        v-if="action"
                        :item="action.data"
                      />
                    </ContextMenuTrigger>
                    <ContextMenuContent
                      class="min-w-[220px] z-30 bg-white rounded-md shadow-md cursor-pointer border-none"
                    >
                      <ContextMenuItem
                        value="New Tab"
                        class="group text-sm p-2 hover:bg-primary/10 "
                        @click="completeTask(action)"
                      >
                        Supprimer
                      </ContextMenuItem>
                    </ContextMenuContent>
                  </ContextMenu>
                </div>
              </transition-group>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
</template>

<script lang="ts" setup>
import {onMounted, onUnmounted} from "vue";
import {TaskItem, useLiveStore} from "@/stores/live.store";
import {DotLottieVue} from "@lottiefiles/dotlottie-vue";
import {OrderCreatedBroadcastEvent} from "@/home/interfaces";
import HomeLiveOrder from "@/home/components/HomeLiveOrder.vue";
import LiveItem, {LiveItemType} from "@/core/interfaces/live/LiveItem";
import HomeLiveInterviewInstance from "@/home/components/HomeLiveInterviewInstance.vue";
import {storeToRefs} from "pinia";
import HomeLiveSurveyAnswerInstance from "@/home/components/HomeLiveSurveyAnswerInstance.vue";
import HomeLiveSharing from "@/home/components/HomeLiveSharing.vue";
import HomeLiveSellinOffer from "@/home/components/HomeLiveSellinOffer.vue";
import HomeLivePatientUser from "@/home/components/HomeLivePatientUser.vue";
import {useToastStore} from "@/stores/toast.store";
import {
    ContextMenu,
    ContextMenuContent,
    ContextMenuItem,
    ContextMenuTrigger,
} from "@/core/components/ui/context-menu";

type ComponentMap = {
    [LiveItemType.ORDER_CREATED]: typeof HomeLiveOrder,
    [LiveItemType.INTERVIEW_INSTANCE]: typeof HomeLiveInterviewInstance,
    [LiveItemType.SURVEY_ANSWER_INSTANCE]: typeof HomeLiveSurveyAnswerInstance,
    [LiveItemType.SHARING]: typeof HomeLiveSharing,
    [LiveItemType.SELLIN]: typeof HomeLiveSellinOffer,
    [LiveItemType.PATIENT_USER]: typeof HomeLivePatientUser
}

const liveStore = useLiveStore();
const {tasks, loading, loaded, refreshing, hasMoreTasks} = storeToRefs(liveStore);
const {showError, showSuccess} = useToastStore();

let removeOrderEventListener: (() => void) | null = null;
let removeSurveyEventListener: (() => void) | null = null;

const components: ComponentMap = {
    [LiveItemType.ORDER_CREATED]: HomeLiveOrder,
    [LiveItemType.INTERVIEW_INSTANCE]: HomeLiveInterviewInstance,
    [LiveItemType.SURVEY_ANSWER_INSTANCE]: HomeLiveSurveyAnswerInstance,
    [LiveItemType.SHARING]: HomeLiveSharing,
    [LiveItemType.SELLIN]: HomeLiveSellinOffer,
    [LiveItemType.PATIENT_USER]: HomeLivePatientUser,
};

/**
 * Resolve the component to use based on the live item given.
 *
 * @param {LiveItem} item
 */
const resolveComponent = (item: LiveItem) => {
    return components[item.type];
};

/**
 * Load the actions.
 */
const loadActions = async () => {
    if (!loaded.value) {
        await liveStore.loadItems();
    } else if (!loading.value && !refreshing.value) {
        await liveStore.refreshItems();
    }
};

/**
 * Complete task.
 */
const completeTask = (item: TaskItem) => {
    if (item.data?.id) {
        liveStore.updateItem(item.data.id, item.type)
            .then(() => showSuccess({content: "L'action a été marquée comme complétée."}))
            .catch(() => showError({content: "Une erreur est survenue lors de la mise à jour de l'action."}));
    }
};

/**
 * On mounted, load the actions and listen for new items.
 */
onMounted(async () => {
    await loadActions();

    removeOrderEventListener = window.ipcRenderer.socketListenEvent('order-created', (event: any, {data}: {
        data: OrderCreatedBroadcastEvent
    }) => {
        liveStore.newBroadcastOrder(data);
    });

    removeSurveyEventListener = window.ipcRenderer.socketListenEvent('survey-completed', () => {
        liveStore.loadItems();
    });
});

/**
 * On unmounted, remove the event listener.
 */
onUnmounted(() => {
    if (removeOrderEventListener) {
        removeOrderEventListener();
    }

    if (removeSurveyEventListener) {
        removeSurveyEventListener();
    }
});
</script>

<style scoped>
/* Slide fade animation. */
.slide-fade-enter-active {
    transition: all 1s ease;
}

.slide-fade-leave-active {
    transition: height 1.5s cubic-bezier(1.0, 0.5, 0.8, 1.0);
}

.slide-fade-enter, .slide-fade-leave-to {
    transform: translateY(-10px);
    opacity: 0;
}

/* List animation. */
.list-action {
    transition: all 1s;
    display: inline-block;
    margin-right: 10px;
}

.list-enter, .list-leave-to {
    opacity: 0;
    transform: translateX(-30px);
}
</style>
